import Axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios';

import { env } from '@/config/env';
import { paths } from '@/config/paths';
import { useOrgStore } from '@/stores/org-store';

import { getIdToken } from './auth';

async function authRequestInterceptor(
  config: InternalAxiosRequestConfig,
  { withOrgId }: { withOrgId: boolean },
) {
  if (config.headers) {
    config.headers.Accept = 'application/json';

    const token = await getIdToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    if (env.WORKSPACE_ID) {
      config.headers['Bey-Workspace-Id'] = env.WORKSPACE_ID;
    }

    if (withOrgId) {
      const orgId = useOrgStore.getState().currentOrgId;
      if (orgId) {
        config.headers['X-Org-Id'] = orgId;
      }
    }
  }

  return config;
}

function createApiClient({
  baseURL,
  withOrgId,
}: {
  baseURL: string;
  withOrgId: boolean;
}): AxiosInstance {
  const instance = Axios.create({ baseURL });

  instance.interceptors.request.use((config) =>
    authRequestInterceptor(config, { withOrgId }),
  );

  instance.interceptors.response.use(
    (response) => response.data,
    (error) => {
      if (error.response?.status === 401) {
        const redirectTo = window.location.pathname;
        window.location.href = paths.auth.login.getHref(redirectTo);
      }

      return Promise.reject(error);
    },
  );

  return instance;
}

export const api = createApiClient({
  baseURL: env.API_URL,
  withOrgId: true,
});

export const apiV3 = createApiClient({
  baseURL: env.API_V3_URL,
  withOrgId: false,
});
